import { useMemo, useState } from 'react';
import clsx from 'clsx';

import { TrackingEvents, TrackingElements, TrackingTabs, useTrack } from '../../tracking';
import { PlatformSection } from '@/common/components';
import { Platforms } from '@/common/constants';
import { usePlatform } from '@/common/hooks';
import { useNewTranslations } from '@/common/translations';
import type { Option } from '@/common/types';
import { ErrorMessage, Filter, FilterChipGroup, PlayersSkeleton } from '@/ui/components';

import { NoDataError, PlayersStats, PlayersStatsLegend, UnableToLoadError } from '../../components';
import { PlayerStatsOptionValues } from '../../constants';
import { useMapPlayersData, usePrematchData } from '../../hooks';
import type { PlayerStatsOption } from '../../types';

import { isPlayersTabEmpty } from './isPlayersTabEmpty';

const TeamFilterOptionValues = {
  ALL: 'all',
  HOME_TEAM: 'homeTeam',
  AWAY_TEAM: 'awayTeam',
} as const;

type TeamFilterOption = (typeof TeamFilterOptionValues)[keyof typeof TeamFilterOptionValues];

export default function PlayersTab() {
  const translate = useNewTranslations();
  const { teams, players, playersStatus, refetchAll } = usePrematchData();
  const platform = usePlatform();
  const track = useTrack();

  const mapPlayersData = useMapPlayersData({
    homeJerseySrc: teams.home.jersey,
    awayJerseySrc: teams.away.jersey,
    teams,
  });

  const mappedPlayersData = useMemo(() => mapPlayersData(players), [players, mapPlayersData]);

  const [teamFilterValue, setTeamFilterValue] = useState<TeamFilterOption>(
    TeamFilterOptionValues.ALL,
  );
  const [playerStatsFilterValue, setPlayerStatsFilterValue] = useState<PlayerStatsOption>(
    PlayerStatsOptionValues.GOALS,
  );

  const teamFilterOptions: Option<TeamFilterOption>[] = [
    { label: translate('PreMatchStats.All'), value: TeamFilterOptionValues.ALL },
    { label: teams.home.name, value: TeamFilterOptionValues.HOME_TEAM },
    { label: teams.away.name, value: TeamFilterOptionValues.AWAY_TEAM },
  ];

  const playerStatsFilterOptions: Option<PlayerStatsOption>[] = [
    { label: translate('PreMatchStats.Goals'), value: PlayerStatsOptionValues.GOALS },
    { label: translate('PreMatchStats.Assists'), value: PlayerStatsOptionValues.ASSISTS },
    { label: translate('PreMatchStats.Shots'), value: PlayerStatsOptionValues.SHOTS },
    { label: translate('PreMatchStats.Cards'), value: PlayerStatsOptionValues.CARDS },
    { label: translate('PreMatchStats.Injured'), value: PlayerStatsOptionValues.INJURED },
  ];

  const handleTeamFilterChange = (value: TeamFilterOption) => {
    track(TrackingEvents.FilterClick, {
      tab: TrackingTabs.Players,
      filter: value,
    });
    setTeamFilterValue(value);
  };

  const handlePlayerStatsFilterChange = (value: PlayerStatsOption) => {
    const elementOrder = playerStatsFilterOptions.findIndex((option) => option.value === value) + 1;
    track(TrackingEvents.FilterClick, {
      tab: TrackingTabs.Players,
      filter: value,
      elementOrder,
    });
    setPlayerStatsFilterValue(value);
  };

  const handleRetryClick = () => {
    track(TrackingEvents.CtaClick, {
      tab: TrackingTabs.Players,
      filter: playerStatsFilterValue,
      element: TrackingElements.Retry,
    });
    refetchAll();
  };

  const playersDataToDisplay = useMemo(() => {
    if (!mappedPlayersData) {
      return [];
    }

    switch (teamFilterValue) {
      case TeamFilterOptionValues.HOME_TEAM:
        return mappedPlayersData[playerStatsFilterValue].filter(({ isHome }) => isHome);
      case TeamFilterOptionValues.AWAY_TEAM:
        return mappedPlayersData[playerStatsFilterValue].filter(({ isHome }) => !isHome);
      case TeamFilterOptionValues.ALL:
      default:
        return mappedPlayersData[playerStatsFilterValue];
    }
  }, [teamFilterValue, mappedPlayersData, playerStatsFilterValue]);

  if (playersStatus === 'pending') {
    return (
      <PlayersSkeleton
        className={clsx({
          'pt-6 px-3 pb-3': platform === Platforms.MOBILE,
          'pt-3': platform !== Platforms.MOBILE,
        })}
      />
    );
  }

  if (playersStatus === 'error') {
    return <UnableToLoadError onRetryClick={handleRetryClick} />;
  }

  if (players === null || isPlayersTabEmpty(players)) {
    return <NoDataError />;
  }

  return (
    <div
      className={clsx('flex flex-col gap-3', {
        'pt-6 px-3 pb-3': platform === Platforms.MOBILE,
        'pt-3': platform !== Platforms.MOBILE,
      })}
    >
      <Filter
        variant="outlined"
        options={teamFilterOptions}
        value={teamFilterValue}
        onValueChange={handleTeamFilterChange}
      />
      <FilterChipGroup
        className={clsx(platform === Platforms.MOBILE && 'mx-[-12px] px-[12px]')}
        isTogglable={false}
        options={playerStatsFilterOptions}
        value={playerStatsFilterValue}
        onValueChange={handlePlayerStatsFilterChange}
        platform={platform}
      />
      {playersDataToDisplay.length ? (
        <PlatformSection>
          <PlayersStats activeFilter={playerStatsFilterValue} playersData={playersDataToDisplay} />
        </PlatformSection>
      ) : (
        <ErrorMessage
          className="h-[351px] w-full px-4"
          title={translate('PreMatchStats.NoDataAvailable')}
          description={translate('PreMatchStats.NoDataAvailableDetails')}
        />
      )}
      <PlayersStatsLegend activeFilter={playerStatsFilterValue} />
    </div>
  );
}
